import { ReactNode } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SilkCover from "../ui/SilkCover";
import NotationCoverWidget from "./covers/NotationCoverWidget";
import CoreCoverWidget from "./covers/CoreCoverWidget";
import SynapseCoverWidget from "./covers/SynapseCoverWidget";
import AtlasCoverWidget from "./covers/AtlasCoverWidget";

type BadgeTone = "flagship" | "api" | "extension" | "soon";

type CoverWidget = "notation" | "core" | "synapse" | "atlas";

const COVER_WIDGETS: Record<CoverWidget, () => JSX.Element> = {
  notation: NotationCoverWidget,
  core: CoreCoverWidget,
  synapse: SynapseCoverWidget,
  atlas: AtlasCoverWidget,
};

type EnterTarget =
  | { kind: "internal"; to: string }
  | { kind: "external"; to: string }
  | null;

interface Product {
  id: "notation" | "core" | "synapse" | "atlas";
  logoText: ReactNode;
  subtitle?: string;
  description: string;
  hoverLabel: string;
  badge: string;
  badgeTone: BadgeTone;
  cover: { base: string; grid?: boolean; widget?: CoverWidget };
  enter: EnterTarget;
}

const baloo = "'Baloo Bhaina 2', sans-serif";

/** Shared sizing so cropped solution logos render at the same visual scale. */
const LOGO_IMG_CLASS =
  "h-8 sm:h-9 w-auto object-contain object-left select-none";

/** Per-card hover glow color (rgb triplet), matching each banner base color. */
const GLOW_RGB: Record<Product["id"], string> = {
  notation: "31, 61, 59",
  core: "66, 156, 203",
  synapse: "242, 234, 203",
  atlas: "236, 239, 241",
};

const PRODUCTS: Product[] = [
  {
    id: "notation",
    logoText: (
      <span
        className="font-normal text-[2.15rem] sm:text-[2.55rem] leading-none"
        style={{ fontFamily: baloo, color: "#439CCB", fontWeight: 400 }}
      >
        notation
      </span>
    ),
    hoverLabel: "Experience notation",
    description:
      "AI clinical documentation partner. Real-time listening turns your session into structured SOAP notes — you treat, we document.",
    badge: "Flagship",
    badgeTone: "flagship",
    cover: { base: "#1F3D3B", widget: "notation" },
    enter: { kind: "internal", to: "/notation" },
  },
  {
    id: "core",
    logoText: (
      <span className="inline-flex items-center gap-2.5">
        <img
          src="/solution-logos/core-card.png"
          alt="core"
          className={LOGO_IMG_CLASS}
          draggable={false}
        />
        <span className="font-productSans text-gray-800 text-base sm:text-lg font-semibold leading-none">
          APIs
        </span>
      </span>
    ),
    hoverLabel: "Explore Core",
    description:
      "The clinical AI behind notation, exposed as modular API endpoints. Tap into transcription, SOAP notes, coding, and tracking — call only what your product needs.",
    badge: "API",
    badgeTone: "api",
    cover: { base: "#429CCB", grid: true, widget: "core" },
    enter: { kind: "external", to: "https://core.fownd.care" },
  },
  {
    id: "synapse",
    logoText: (
      <img
        src="/solution-logos/synapse-card.png"
        alt="synapse"
        className={LOGO_IMG_CLASS}
        draggable={false}
      />
    ),
    hoverLabel: "Understand Synapse",
    description:
      "Transfer Notation data to any EMR with the Synapse browser extension.",
    badge: "Coming soon",
    badgeTone: "soon",
    cover: { base: "#f2eacb", widget: "synapse" },
    enter: null,
  },
  {
    id: "atlas",
    logoText: (
      <img
        src="/solution-logos/atlas-card.png"
        alt="atlas"
        className={LOGO_IMG_CLASS}
        draggable={false}
      />
    ),
    subtitle: "Interactive anatomy",
    hoverLabel: "Visualize anatomy",
    description:
      "Free interactive anatomy reference for clinicians and educators. Dry needling education modules are on the way.",
    badge: "Coming soon",
    badgeTone: "soon",
    cover: { base: "#ECEFF1", widget: "atlas" },
    enter: null,
  },
];

const badgeStyles: Record<BadgeTone, string> = {
  flagship: "bg-[#619ed3]/10 text-[#3d7aa8] border-[#619ed3]/20",
  api: "bg-[#486633]/10 text-[#486633] border-[#486633]/20",
  extension: "bg-gray-100 text-gray-600 border-gray-200",
  soon: "bg-gray-100 text-gray-500 border-gray-200",
};

function TileBadge({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badgeStyles[tone]}`}
    >
      {children}
    </span>
  );
}


interface CardProps {
  product: Product;
  layout: "large" | "wide" | "default";
  onSelect: () => void;
}

function ProductCard({ product, layout, onSelect }: CardProps) {
  const isWide = layout === "wide";
  const isLarge = layout === "large";
  const interactive = product.enter !== null;

  const coverClass = isLarge
    ? "flex-1 min-h-[240px]"
    : isWide
    ? "sm:w-[42%] min-h-[180px]"
    : "h-40 sm:h-44";

  return (
    <motion.div
      onClick={interactive ? onSelect : undefined}
      whileHover={interactive ? { y: -8 } : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : -1}
      aria-label={interactive ? product.hoverLabel : undefined}
      style={{ ["--bento-glow" as string]: GLOW_RGB[product.id] } as React.CSSProperties}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      className={`group/card relative h-full rounded-3xl bg-white overflow-hidden flex ${
        interactive
          ? "bento-card cursor-pointer"
          : "cursor-default shadow-[0_6px_28px_-10px_rgba(31,61,59,0.12)]"
      } ${isWide ? "flex-col sm:flex-row" : "flex-col"}`}
    >
      <SilkCover base={product.cover.base} grid={product.cover.grid} className={coverClass}>
        {product.cover.widget
          ? (() => {
              const Widget = COVER_WIDGETS[product.cover.widget];
              return <Widget />;
            })()
          : null}
      </SilkCover>

      <div
        className={`flex flex-col gap-2.5 p-5 sm:p-6 ${
          isWide ? "flex-1 justify-center" : ""
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h3 className="text-3xl sm:text-4xl font-normal leading-none">
              {product.logoText}
            </h3>
            {product.subtitle ? (
              <p className="text-sm text-[#619ed3] font-medium truncate">
                {product.subtitle}
              </p>
            ) : null}
          </div>
          <TileBadge tone={product.badgeTone}>{product.badge}</TileBadge>
        </div>
        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
          {product.description}
        </p>
      </div>
    </motion.div>
  );
}

export default function ProductBentoGrid() {
  const navigate = useNavigate();

  const handleSelect = (product: Product) => {
    if (!product.enter) return;
    if (product.enter.kind === "internal") {
      navigate(product.enter.to);
    } else {
      window.open(product.enter.to, "_blank", "noopener,noreferrer");
    }
  };

  const gridLayout = (id: string): "large" | "wide" | "default" => {
    if (id === "notation") return "large";
    if (id === "atlas") return "wide";
    return "default";
  };

  const wrapperClass = (id: string) => {
    if (id === "notation") return "md:row-span-2";
    if (id === "atlas") return "md:col-span-2";
    return "";
  };

  return (
    <div className="mb-16 max-w-6xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)] gap-4 lg:gap-5 auto-rows-auto">
        {PRODUCTS.map((product) => (
          <div key={product.id} className={wrapperClass(product.id)}>
            <ProductCard
              product={product}
              layout={gridLayout(product.id)}
              onSelect={() => handleSelect(product)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
